import { useEffect, useMemo, useReducer } from "react";

import { useChatStore, selectOlderMessages } from "@stores/chat.store";
import type { TMessageListState } from "@stores/chat-domain.types";
import { useChatMessages } from "@hooks/api/chat-websocket";

import { CHAT_MESSAGE_STATUS } from "@/utils/constants";
import { isMyMessage } from "@/utils/helpers";
import {
	chatMessageStatusUpdater,
	chatMessageStatusByOwner,
	findFirstChatMessagePendingStatus,
} from "./utils/conversation-messages.message-status";
import { messagesReducer } from "./utils/conversation-messages.helpers";

/** Gerencia o estado local de mensagens (append, prepend, status) via reducer sincronizado com a store. */
const useConversationMessages = (myId: number) => {
	const messageListSource = useChatStore((s) => s.messageList.source);
	const messageListPagination = useChatStore((s) => s.messageList.pagination);
	const messageListChatId = useChatStore((s) => s.messageList.chatId);
	const newMessageFetched = useChatStore((s) => s.newMessage.fetched);
	const newMessageChatId = useChatStore((s) => s.newMessage.chatId);
	const newMessageItsMyMessage = useChatStore((s) => s.newMessage.itsMyMessage);
	const newMessageSource = useChatStore((s) => s.newMessage.source);
	const messageStatusFetched = useChatStore((s) => s.messageStatus.fetched);
	const messageStatusSource = useChatStore((s) => s.messageStatus.source);
	const { updateMessageStatus } = useChatMessages();

	const { source: olderMessages, loadedOlderMessages } = useMemo(
		() =>
			selectOlderMessages(
				{ source: messageListSource, pagination: messageListPagination } as TMessageListState,
				myId,
			),
		[messageListSource, messageListPagination, myId],
	);

	const [messages, dispatchMessages] = useReducer(messagesReducer, {
		source: olderMessages || [],
		newMessage: false,
	});

	useEffect(() => {
		if (newMessageFetched && newMessageChatId === messageListChatId) {
			if (newMessageItsMyMessage) {
				dispatchMessages({
					type: "APPEND_MY_MESSAGES",
					payload: newMessageSource,
				});
			} else {
				dispatchMessages({
					type: "APPEND_OTHER_MESSAGES",
					payload: newMessageSource,
				});
			}
		}
	}, [newMessageFetched, newMessageChatId, newMessageItsMyMessage, newMessageSource, messageListChatId]);

	useEffect(() => {
		if (messageStatusFetched) {
			const { myStatus, othersStatus } = chatMessageStatusByOwner(
				messageStatusSource,
				messageListChatId ?? '',
				myId,
			);

			if (myStatus.length) {
				const messageHistory = [...messages.source];
				let index = findFirstChatMessagePendingStatus(messageHistory);

				while (myStatus.length && index < messageHistory.length) {
					const newStatus = chatMessageStatusUpdater(
						messageHistory[index]?.statusMessage ?? "",
						myStatus[0].status ?? "",
					);
					if (
						messageHistory[index]?.statusMessage &&
						messageHistory[index]?.statusMessage !== CHAT_MESSAGE_STATUS.read &&
						newStatus &&
						isMyMessage(messageHistory[index].senderId, myId)
					) {
						messageHistory[index].statusMessage = newStatus;
						myStatus.shift();
					}
					index++;
				}

				dispatchMessages({ type: "UPDATE_STATUSES", payload: messageHistory });
			}
			if (othersStatus.length) {
				const { id: chatId, senderId: UserIdToUpdateMessage } = othersStatus[0];
				updateMessageStatus({
					chatId,
					UserIdToUpdateMessage: UserIdToUpdateMessage ?? "",
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [messageStatusFetched, messageStatusSource, messageListChatId, myId, updateMessageStatus]);

	useEffect(() => {
		if (loadedOlderMessages)
			dispatchMessages({ type: "PREPEND_OLDER", payload: olderMessages });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadedOlderMessages]);

	return {
		messages: messages.source,
		newMessage: messages.newMessage,
	};
};

export { useConversationMessages };
